import { useEffect, useState } from "react";
import { Trophy, Settings, PlusCircle, IndianRupee, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const EMPTY_CH = { name: "", description: "", target: "", start_date: "", end_date: "", reward_type: "custom", reward_description: "", banner: "", reward_image: "", status: "draft" };
const TABS = [["challenges", "Challenges", Trophy], ["settings", "Leaderboard Settings", Settings], ["adjust", "Earning Corrections", IndianRupee]];

export default function RootChallenges() {
  const [tab, setTab] = useState("challenges");
  const [challenges, setChallenges] = useState(null);
  const [form, setForm] = useState(EMPTY_CH);
  const [editId, setEditId] = useState("");
  const [settings, setSettings] = useState(null);
  const [partners, setPartners] = useState(null);
  const [adj, setAdj] = useState({ partner_id: "", amount: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");

  const loadCh = () => api.get("/root/challenges").then((r) => setChallenges(r.data.challenges)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { loadCh(); }, []);
  useEffect(() => {
    if (tab === "settings" && !settings) api.get("/root/leaderboard/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(errMsg(e)));
    if (tab === "adjust" && !partners) api.get("/root/leaderboard/partners").then((r) => setPartners(r.data.partners)).catch((e) => toast.error(errMsg(e)));
  }, [tab]);

  const upload = async (e, field) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads", fd);
      setForm((p) => ({ ...p, [field]: data.url }));
      toast.success("Image uploaded");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setUploading(""); }
  };

  const saveChallenge = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, target: Number(form.target) };
      if (editId) {
        await api.patch(`/root/challenges/${editId}`, payload);
        toast.success("Challenge updated");
      } else {
        await api.post("/root/challenges", payload);
        toast.success("Challenge created");
      }
      setForm(EMPTY_CH); setEditId(""); loadCh();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const setStatus = async (c, status) => {
    await api.patch(`/root/challenges/${c.id}`, { status }).then(() => { toast.success(`Status → ${status}`); loadCh(); }).catch((e) => toast.error(errMsg(e)));
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/root/leaderboard/settings", settings);
      setSettings(data); toast.success("Leaderboard settings saved (audited)");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const doAdjust = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/root/leaderboard/adjust", { ...adj, amount: Number(adj.amount) });
      toast.success("Earning correction recorded with full audit trail");
      setAdj({ partner_id: "", amount: "", reason: "" });
      api.get("/root/leaderboard/partners").then((r) => setPartners(r.data.partners));
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="root-challenges-page">
      <PageHeader title="Challenges & Leaderboard" subtitle="Top Freelancers earnings config, earning corrections (audited) and challenge campaigns." testid="root-challenges-title" />

      <div className="flex gap-2 flex-wrap mb-8">
        {TABS.map(([k, l, Ic]) => (
          <button key={k} data-testid={`ch-tab-${k}`} onClick={() => setTab(k)}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border transition-colors ${tab === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <Ic className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>

      {tab === "challenges" && (
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <form data-testid="challenge-form" onSubmit={saveChallenge} className="lg:col-span-2 bg-card border border-border rounded-2xl p-6 space-y-4">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><PlusCircle className="h-5 w-5" /> {editId ? "Edit Challenge" : "Create Challenge"}</h3>
            <div className="space-y-1.5"><Label className="overline-label">Challenge Name</Label>
              <Input data-testid="ch-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-md bg-background" placeholder="₹10,000 Growth Challenge" /></div>
            <div className="space-y-1.5"><Label className="overline-label">Description</Label>
              <textarea data-testid="ch-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm min-h-16" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="overline-label">Target (₹)</Label>
                <Input data-testid="ch-target" required type="number" min="1" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} className="h-11 rounded-md bg-background" /></div>
              <div className="space-y-1.5"><Label className="overline-label">Status</Label>
                <select data-testid="ch-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="draft">Draft</option><option value="scheduled">Scheduled</option><option value="live">Live</option>
                </select></div>
              <div className="space-y-1.5"><Label className="overline-label">Start Date</Label>
                <Input data-testid="ch-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-11 rounded-md bg-background" /></div>
              <div className="space-y-1.5"><Label className="overline-label">End Date</Label>
                <Input data-testid="ch-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="h-11 rounded-md bg-background" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="overline-label">Reward Type</Label>
                <select data-testid="ch-reward-type" value={form.reward_type} onChange={(e) => setForm({ ...form, reward_type: e.target.value })} className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="vacation">Vacation</option><option value="travel">Travel</option><option value="gift">Gift</option>
                  <option value="gold_medal">Gold Medal</option><option value="cash">Cash-equivalent</option><option value="custom">Custom Reward</option>
                </select></div>
              <div className="space-y-1.5"><Label className="overline-label">Reward Description</Label>
                <Input data-testid="ch-reward-desc" value={form.reward_description} onChange={(e) => setForm({ ...form, reward_description: e.target.value })} className="h-11 rounded-md bg-background" placeholder="3-day Goa trip" /></div>
            </div>
            <div className="flex gap-3">
              <label className="flex-1 cursor-pointer inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-border h-11 text-xs font-bold hover:bg-muted">
                {uploading === "banner" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} {form.banner ? "Banner ✓" : "Upload Banner"}
                <input data-testid="ch-banner" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "banner")} />
              </label>
              <label className="flex-1 cursor-pointer inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-border h-11 text-xs font-bold hover:bg-muted">
                {uploading === "reward_image" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} {form.reward_image ? "Reward Img ✓" : "Reward Image"}
                <input data-testid="ch-reward-image" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "reward_image")} />
              </label>
            </div>
            <div className="flex gap-3">
              <Button data-testid="ch-save" type="submit" disabled={busy} className="rounded-full font-bold flex-1">{busy ? "Saving..." : editId ? "Update Challenge" : "Create Challenge"}</Button>
              {editId && <Button type="button" variant="outline" data-testid="ch-cancel-edit" onClick={() => { setEditId(""); setForm(EMPTY_CH); }} className="rounded-full font-bold">Cancel</Button>}
            </div>
          </form>

          <div className="lg:col-span-3">
            {!challenges ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> :
             challenges.length === 0 ? <EmptyState icon={Trophy} title="No challenges yet" body="Create your first earning challenge campaign." /> : (
              <TableShell head={["Challenge", "Target", "Dates", "Status", "Completed By", "Actions"]} testid="challenges-table">
                {challenges.map((c) => (
                  <TR key={c.id} testid={`ch-row-${c.id}`}>
                    <TD><p className="font-semibold">{c.name}</p><p className="text-xs text-muted-foreground">{c.reward_description || c.reward_type}</p></TD>
                    <TD className="font-bold">{fmtINR(c.target)}</TD>
                    <TD className="text-xs text-muted-foreground">{c.start_date ? fmtDateTime(c.start_date).split(",")[0] : "—"} → {c.end_date ? fmtDateTime(c.end_date).split(",")[0] : "open"}</TD>
                    <TD><StatusBadge status={c.status} /></TD>
                    <TD>{c.completions}</TD>
                    <TD>
                      <div className="flex gap-2 flex-wrap">
                        <button data-testid={`ch-edit-${c.id}`} onClick={() => { setEditId(c.id); setForm({ ...EMPTY_CH, ...c, start_date: (c.start_date || "").slice(0, 10), end_date: (c.end_date || "").slice(0, 10) }); }}
                          className="h-8 px-3 rounded-lg border border-border text-xs font-bold hover:bg-muted">Edit</button>
                        {c.status !== "live" && <button data-testid={`ch-live-${c.id}`} onClick={() => setStatus(c, "live")} className="h-8 px-3 rounded-lg bg-emerald-500/15 text-emerald-600 text-xs font-bold hover:bg-emerald-500/25">Go Live</button>}
                        {c.status === "live" && <button data-testid={`ch-complete-${c.id}`} onClick={() => setStatus(c, "completed")} className="h-8 px-3 rounded-lg bg-blue-500/15 text-blue-600 text-xs font-bold hover:bg-blue-500/25">Complete</button>}
                        {!["cancelled", "completed"].includes(c.status) && <button data-testid={`ch-cancel-${c.id}`} onClick={() => setStatus(c, "cancelled")} className="h-8 px-3 rounded-lg bg-red-500/15 text-red-600 text-xs font-bold hover:bg-red-500/25">Cancel</button>}
                      </div>
                    </TD>
                  </TR>
                ))}
              </TableShell>
            )}
          </div>
        </div>
      )}

      {tab === "settings" && (
        !settings ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> : (
          <form data-testid="lb-settings-form" onSubmit={saveSettings} className="bg-card border border-border rounded-2xl p-6 grid sm:grid-cols-2 gap-5 max-w-3xl">
            <div className="space-y-1.5">
              <Label className="overline-label">Default Website Project Earning (₹)</Label>
              <Input data-testid="lb-website-earning" type="number" min="0" value={settings.default_website_earning} onChange={(e) => setSettings({ ...settings, default_website_earning: e.target.value })} className="h-11 rounded-md bg-background" />
              <p className="text-[11px] text-muted-foreground">Verified earning per completed (active) website project.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="overline-label">Monthly Period Mode</Label>
              <select data-testid="lb-month-mode" value={settings.month_mode} onChange={(e) => setSettings({ ...settings, month_mode: e.target.value })} className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="last30">Last 30 Days</option><option value="calendar">Calendar Month</option>
              </select>
            </div>
            {[["include_crm", "Include CRM commissions in earnings"], ["show_leaderboard", "Leaderboard visible to partners"], ["celebration", "Celebration effects (confetti/glow)"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-3 cursor-pointer">
                <input data-testid={`lb-${k}`} type="checkbox" checked={Boolean(settings[k])} onChange={(e) => setSettings({ ...settings, [k]: e.target.checked })} className="h-5 w-5 accent-primary" />
                <span className="text-sm font-bold">{l}</span>
              </label>
            ))}
            <div className="sm:col-span-2">
              <p className="overline-label mb-2">Achievement Tiers (target ₹ thresholds)</p>
              <div className="grid grid-cols-4 gap-3">
                {["bronze", "silver", "gold", "platinum"].map((t) => (
                  <div key={t} className="space-y-1">
                    <Label className="overline-label capitalize">{t}</Label>
                    <Input data-testid={`lb-tier-${t}`} type="number" min="0" value={settings.tiers?.[t] ?? ""} onChange={(e) => setSettings({ ...settings, tiers: { ...settings.tiers, [t]: e.target.value } })} className="h-11 rounded-md bg-background" />
                  </div>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Button data-testid="lb-settings-save" type="submit" disabled={busy} className="rounded-full font-bold">{busy ? "Saving..." : "Save Settings"}</Button>
            </div>
          </form>
        )
      )}

      {tab === "adjust" && (
        <div className="grid lg:grid-cols-3 gap-8 items-start max-w-6xl">
          <form data-testid="adjust-form" onSubmit={doAdjust} className="bg-card border border-border rounded-2xl p-6 space-y-4">
            <h3 className="font-display font-bold text-lg">Manual Earning Correction</h3>
            <p className="text-xs text-muted-foreground">Every correction is permanently recorded with your identity, reason and timestamp in the audit log. No silent manipulation.</p>
            <div className="space-y-1.5"><Label className="overline-label">Partner</Label>
              <select data-testid="adjust-partner" required value={adj.partner_id} onChange={(e) => setAdj({ ...adj, partner_id: e.target.value })} className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Select partner…</option>
                {(partners || []).map((p) => <option key={p.partner_id} value={p.partner_id}>{p.name} ({p.email})</option>)}
              </select></div>
            <div className="space-y-1.5"><Label className="overline-label">Amount (₹, negative to deduct)</Label>
              <Input data-testid="adjust-amount" required type="number" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} className="h-11 rounded-md bg-background" /></div>
            <div className="space-y-1.5"><Label className="overline-label">Reason (required)</Label>
              <textarea data-testid="adjust-reason" required value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2.5 text-sm min-h-16" /></div>
            <Button data-testid="adjust-submit" type="submit" disabled={busy} className="rounded-full font-bold w-full">{busy ? "Recording..." : "Record Correction (Audited)"}</Button>
          </form>
          <div className="lg:col-span-2">
            {!partners ? <div className="bg-card border border-border rounded-2xl p-8"><LoadingRows rows={3} /></div> : (
              <TableShell head={["Partner", "Email", "Websites", "Website ₹", "CRM ₹", "Adjustments", "Total Verified"]} testid="partners-earnings-table">
                {partners.map((p) => (
                  <TR key={p.partner_id} testid={`pe-${p.partner_id}`}>
                    <TD className="font-semibold">{p.name}</TD>
                    <TD className="text-xs text-muted-foreground">{p.email}</TD>
                    <TD>{p.websites}</TD>
                    <TD>{fmtINR(p.website_earnings)}</TD>
                    <TD>{fmtINR(p.crm_earnings)}</TD>
                    <TD className={p.adjustments ? "text-amber-600 font-bold" : ""}>{fmtINR(p.adjustments)}</TD>
                    <TD className="font-bold">{fmtINR(p.total)}</TD>
                  </TR>
                ))}
              </TableShell>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
